import * as Sentry from '@sentry/react';

const KIBO_CHAT_ID = 'c2e17b36-fe3c-1c23-0963-3121221923fc';
const KIBO_SDK_URL = 'https://cdn.kibo.ai/chat.js';

/**
 * The SDK is ~250 KB served with `cache-control: no-cache`, so it is fetched over the
 * network on every single page load — there is no cached copy to fall back on when a
 * request fails. Without a retry a single hiccup means no chat for the whole session.
 */
const MAX_ATTEMPTS = 3;
const RETRY_BASE_MS = 2_000;

let injected = false;
let attempt = 0;

const injectScript = () => {
  const script = document.createElement('script');
  script.src = KIBO_SDK_URL;
  script.async = true;

  script.onerror = () => {
    script.remove();
    attempt += 1;

    if (attempt >= MAX_ATTEMPTS) {
      // Nothing renders when the bundle never executes, and the SDK has no failure
      // channel of its own, so the only way this is ever noticed is from here.
      Sentry.captureException(new Error('Kibo chat SDK failed to load'), {
        tags: { integration: 'kibo' },
        extra: { url: KIBO_SDK_URL, attempts: attempt },
      });
      return;
    }

    window.setTimeout(injectScript, RETRY_BASE_MS * 2 ** (attempt - 1));
  };

  document.head.appendChild(script);
};

/**
 * Injects the Kibo chat SDK once per page load and returns its config object.
 *
 * The caller must have every identity field ready before calling: the SDK snapshots
 * `window.$kibo` a single time when the bundle executes and never re-reads it, so
 * anything written afterwards is silently dropped.
 */
export const loadKibo = (): KiboConfig => {
  const config = (window.$kibo ??= {});

  if (injected) return config;
  injected = true;

  config.chatId = KIBO_CHAT_ID;

  injectScript();

  return config;
};

/**
 * Ends the signed-in visitor's chat session, for the sign-outs that do not reload the page.
 *
 * `reset()` is installed by the SDK on boot and drops the stored `conversationId`, which is
 * what actually stops the next person on this browser from reopening the previous account's
 * support thread. Clearing the identity fields is the belt-and-braces half: it is a no-op
 * once the SDK has snapshotted the config, but it still matters in the narrow window where
 * a sign-out lands before the bundle has executed, which would otherwise register the
 * contact of an account that is already gone.
 */
export const endKiboSession = (): void => {
  const config = window.$kibo;
  if (!config) return;

  config.reset?.();

  delete config.userId;
  delete config.name;
  delete config.email;
  delete config.picture;
};
